import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import FacilitySearchSelect from '../components/FacilitySearchSelect';
import InsuranceSelect, { parseInsurance } from '../components/InsuranceSelect';
import {
  getMyFacility, updateMyFacility,
  getFacilityApiKeys, createFacilityApiKey, revokeFacilityApiKey,
} from '../api';

const BED_TYPES = [
  { key: 'beds_eval_treatment_adult',    label: 'Eval & Treatment (Adult)' },
  { key: 'beds_eval_treatment_youth',    label: 'Eval & Treatment (Youth)' },
  { key: 'beds_eval_treatment_clip',     label: 'Eval & Treatment (CLIP)' },
  { key: 'beds_crisis_stabilization',    label: 'Crisis Stabilization' },
  { key: 'beds_intensive_bh',            label: 'Intensive BH Treatment' },
  { key: 'beds_competency_restoration',  label: 'Competency Restoration' },
  { key: 'beds_mh_peer_respite',         label: 'MH Peer Respite' },
  { key: 'beds_withdrawal_adult',        label: 'Withdrawal Mgmt (Adult)' },
  { key: 'beds_withdrawal_youth',        label: 'Withdrawal Mgmt (Youth)' },
  { key: 'beds_secure_withdrawal_adult', label: 'Secure Withdrawal (Adult)' },
  { key: 'beds_secure_withdrawal_youth', label: 'Secure Withdrawal (Youth)' },
  { key: 'beds_res_sud_adult',           label: 'Residential SUD (Adult)' },
  { key: 'beds_res_sud_youth',           label: 'Residential SUD (Youth)' },
];

function isLicensedBedType(facility, key) {
  return Number(facility?.[key] || 0) > 0;
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-5">
      <h2 className="text-base font-semibold text-gray-800 mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, optional, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {optional && <span className="text-gray-400 font-normal"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400';
const textareaCls = `${inputCls} resize-none`;

function formatKeyDate(ts) {
  return ts ? new Date(ts).toLocaleDateString() : 'never';
}

function ApiAccessSection({ facilityId }) {
  const [keys, setKeys]       = useState([]);
  const [label, setLabel]     = useState('');
  const [newKey, setNewKey]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [copied, setCopied]   = useState(false);
  const [keyError, setKeyError] = useState('');

  const load = () => getFacilityApiKeys(facilityId).then(setKeys).catch(() => {});

  useEffect(() => {
    getFacilityApiKeys(facilityId).then(setKeys).catch(() => {});
  }, [facilityId]);

  const handleCreate = async () => {
    setBusy(true);
    setKeyError('');
    try {
      const created = await createFacilityApiKey(label, facilityId);
      setNewKey(created);
      setLabel('');
      await load();
    } catch (err) {
      setKeyError(err?.response?.data?.error || 'Failed to create key.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this key? Any system using it will stop working immediately.')) return;
    try {
      await revokeFacilityApiKey(id, facilityId);
      if (newKey?.id === id) setNewKey(null);
      await load();
    } catch (err) {
      setKeyError(err?.response?.data?.error || 'Failed to revoke key.');
    }
  };

  const handleCopy = () => {
    if (!newKey?.key || !navigator.clipboard) return;
    navigator.clipboard.writeText(newKey.key).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <Section title="API Access">
      <p className="text-sm text-gray-600">
        Let your EHR, census system, or a script push bed availability to this listing automatically.
        Each key can only update this facility. See the{' '}
        <Link to="/api-docs" className="text-blue-600 hover:underline font-medium">API documentation</Link>.
      </p>

      {newKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 space-y-2">
          <p className="text-sm font-medium text-amber-800">
            Copy this key now — it will not be shown again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5 text-xs text-gray-800 break-all">
              {newKey.key}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {keys.length > 0 ? (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {keys.map((k) => (
            <li key={k.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm text-gray-800 truncate">
                  <code className="text-xs bg-gray-100 rounded px-1.5 py-0.5 mr-2">{k.key_prefix}…</code>
                  {k.label || <span className="text-gray-400">Untitled key</span>}
                </p>
                <p className="text-xs text-gray-400">
                  Created {formatKeyDate(k.created_at)} · Last used {formatKeyDate(k.last_used_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(k.id)}
                className="text-xs text-gray-400 hover:text-red-600 underline underline-offset-2 flex-shrink-0"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400">No active keys.</p>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className={inputCls}
          placeholder="Key name, e.g. “Kipu integration”"
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          className="text-sm font-semibold px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-900 text-white whitespace-nowrap disabled:opacity-60"
        >
          {busy ? 'Creating…' : 'Generate key'}
        </button>
      </div>

      {keyError && <p className="text-sm text-red-600">{keyError}</p>}
    </Section>
  );
}

export default function MyFacility() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [facility, setFacility]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [saved, setSaved]                 = useState(false);
  const [error, setError]                 = useState('');
  const [notLinked, setNotLinked]         = useState(false);
  const [adminSelected, setAdminSelected] = useState(null); // { id, agency_name }

  // Form state
  const [form, setForm] = useState({
    contact_name: '', contact_title: '', contact_phone: '', contact_email: '',
    physical_street: '', physical_city: '', physical_zip: '',
    website_url: '', phone_number: '', gender_served: '',
    description: '', languages_spoken: '', insurance_accepted: [],
    diagnoses_accepted: '', referral_requirements: '',
    is_wheelchair_accessible: false, is_ada_compliant: false, has_telehealth: false,
    is_accepting_referrals: false, serves_ppw: false,
  });
  const [bedAvailability, setBedAvailability] = useState({});

  useEffect(() => {
    // Admins must select a facility first
    if (isAdmin && !adminSelected) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotLinked(false);
    getMyFacility(isAdmin ? adminSelected?.id : undefined)
      .then((data) => {
        setFacility(data);
        // Populate form from existing data
        setForm({
          contact_name:              data.contact_name      || '',
          contact_title:             data.contact_title     || '',
          contact_phone:             data.contact_phone     || '',
          contact_email:             data.contact_email     || '',
          physical_street:           data.physical_street   || '',
          physical_city:             data.physical_city     || '',
          physical_zip:              data.physical_zip      || '',
          website_url:               data.website_url       || '',
          phone_number:              data.phone_number      || '',
          gender_served:             data.gender_served     || '',
          description:               data.description       || '',
          languages_spoken:          data.languages_spoken  || '',
          insurance_accepted:        parseInsurance(data.insurance_accepted),
          diagnoses_accepted:        data.diagnoses_accepted || '',
          referral_requirements:     data.referral_requirements || '',
          is_wheelchair_accessible:  !!data.is_wheelchair_accessible,
          is_ada_compliant:          !!data.is_ada_compliant,
          has_telehealth:            !!data.has_telehealth,
          is_accepting_referrals:    !!data.is_accepting_referrals,
          serves_ppw:                !!data.serves_ppw,
        });
        // Parse self_reported_beds JSON
        if (data.self_reported_beds) {
          try {
            const parsed = JSON.parse(data.self_reported_beds);
            const filtered = Object.fromEntries(
              Object.entries(parsed).filter(([key, value]) =>
                isLicensedBedType(data, key) && value !== undefined && value !== null && value !== ''
              )
            );
            setBedAvailability(filtered);
          } catch {}
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotLinked(true);
      })
      .finally(() => setLoading(false));
  }, [isAdmin, adminSelected]);

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const setBed = (k) => (e) => {
    const val = e.target.value === '' ? undefined : Number(e.target.value);
    setBedAvailability((b) => {
      const next = { ...b };
      if (val === undefined) delete next[k];
      else next[k] = val;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = {
        ...form,
        insurance_accepted: form.insurance_accepted.length > 0
          ? JSON.stringify(form.insurance_accepted)
          : '',
        is_wheelchair_accessible: form.is_wheelchair_accessible ? 1 : 0,
        is_ada_compliant:         form.is_ada_compliant ? 1 : 0,
        has_telehealth:           form.has_telehealth ? 1 : 0,
        is_accepting_referrals:   form.is_accepting_referrals ? 1 : 0,
        serves_ppw:               form.serves_ppw ? 1 : 0,
        ...(isAdmin && adminSelected ? { facility_id: adminSelected.id } : {}),
      };
      if (facility?.has_inpatient && Object.keys(bedAvailability).length > 0) {
        const filteredBeds = Object.fromEntries(
          Object.entries(bedAvailability).filter(([key, value]) =>
            isLicensedBedType(facility, key) && value !== undefined && value !== null && value !== ''
          )
        );
        if (Object.keys(filteredBeds).length > 0) {
          payload.self_reported_beds = JSON.stringify(filteredBeds);
        }
      }
      await updateMyFacility(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header subtitle="My Facility" />
        <div className="flex-1 flex items-center justify-center">
          <svg className="animate-spin w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        </div>
      </div>
    );
  }

  // Admin: show facility picker before any facility is selected
  if (isAdmin && !adminSelected) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header subtitle="My Facility" />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-8 py-10 max-w-md w-full">
            <p className="text-gray-700 font-semibold mb-1">Edit a Facility</p>
            <p className="text-sm text-gray-500 mb-5">Search for any facility to edit its profile.</p>
            <FacilitySearchSelect
              selected={null}
              onSelect={(f) => f && setAdminSelected(f)}
              placeholder="Search facilities…"
            />
          </div>
        </div>
      </div>
    );
  }

  if (notLinked) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header subtitle="My Facility" />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-8 py-10 max-w-md w-full text-center">
            <p className="text-gray-700 font-medium mb-2">No linked facility</p>
            <p className="text-sm text-gray-500 mb-5">
              Your account isn't linked to a facility listing yet, or your link request is pending admin approval.
            </p>
            <button onClick={() => navigate('/account')}
              className="text-sm text-blue-600 hover:underline">
              ← Back to account
            </button>
          </div>
        </div>
      </div>
    );
  }

  const lastUpdated = facility?.self_reported_updated_at
    ? new Date(facility.self_reported_updated_at).toLocaleDateString()
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header subtitle="My Facility" />

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{facility?.agency_name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {facility?.physical_county} County
              {lastUpdated && ` · Last updated ${lastUpdated}`}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => { setAdminSelected(null); setFacility(null); setLoading(true); }}
              className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 flex-shrink-0 mt-1"
            >
              Change facility
            </button>
          )}
        </div>

        <div className="space-y-5">

          {/* Contact Person */}
          <Section title="Point of Contact">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" optional>
                <input type="text" value={form.contact_name} onChange={set('contact_name')} className={inputCls} placeholder="Jane Doe" />
              </Field>
              <Field label="Title" optional>
                <input type="text" value={form.contact_title} onChange={set('contact_title')} className={inputCls} placeholder="Intake Coordinator" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Direct Phone" optional>
                <input type="text" value={form.contact_phone} onChange={set('contact_phone')} className={inputCls} placeholder="(360) 555-0100" />
              </Field>
              <Field label="Email" optional>
                <input type="email" value={form.contact_email} onChange={set('contact_email')} className={inputCls} placeholder="intake@agency.org" />
              </Field>
            </div>
          </Section>

          {/* Address */}
          <Section title="Address">
            <Field label="Street" optional>
              <input type="text" value={form.physical_street} onChange={set('physical_street')} className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" optional>
                <input type="text" value={form.physical_city} onChange={set('physical_city')} className={inputCls} />
              </Field>
              <Field label="Zip" optional>
                <input type="text" value={form.physical_zip} onChange={set('physical_zip')} className={inputCls} />
              </Field>
            </div>
            <p className="text-xs text-gray-400">County is sourced from the WA DOH Greenbook and cannot be edited here.</p>
          </Section>

          {/* Profile */}
          <Section title="Profile">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Website" optional>
                <input type="url" value={form.website_url} onChange={set('website_url')} className={inputCls} placeholder="https://www.agency.org" />
              </Field>
              <Field label="Main Phone" optional>
                <input type="text" value={form.phone_number} onChange={set('phone_number')} className={inputCls} placeholder="(360) 555-0100" />
              </Field>
            </div>
            <Field label="Program Description" optional>
              <textarea value={form.description} onChange={set('description')} rows={4}
                className={textareaCls}
                placeholder="A few sentences about your approach, philosophy, and specialties…" />
            </Field>
            <Field label="Languages Spoken" optional>
              <input type="text" value={form.languages_spoken} onChange={set('languages_spoken')} className={inputCls}
                placeholder="e.g. English, Spanish, Vietnamese" />
            </Field>
            <Field label="Insurance Accepted" optional>
              <InsuranceSelect
                selected={form.insurance_accepted}
                onChange={(arr) => setForm((f) => ({ ...f, insurance_accepted: arr }))}
                inputCls={inputCls}
              />
            </Field>
          </Section>

          {/* Services */}
          <Section title="Services">
            <Field label="Gender Served" optional>
              <select value={form.gender_served} onChange={set('gender_served')} className={`${inputCls} bg-white`}>
                <option value="">Not specified</option>
                <option value="All genders">All genders</option>
                <option value="Male only">Male only</option>
                <option value="Female only">Female only</option>
              </select>
            </Field>
            <Field label="Diagnoses Accepted" optional>
              <textarea value={form.diagnoses_accepted} onChange={set('diagnoses_accepted')} rows={3}
                className={textareaCls}
                placeholder="What diagnoses or conditions you can and can't treat…" />
            </Field>
            <Field label="Referral Requirements" optional>
              <textarea value={form.referral_requirements} onChange={set('referral_requirements')} rows={3}
                className={textareaCls}
                placeholder="What paperwork, assessments, or documentation referrers need to send…" />
            </Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.serves_ppw} onChange={set('serves_ppw')}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-gray-700">Serves pregnant / parenting women (PPW)</span>
            </label>
          </Section>

          {/* Accessibility & Options */}
          <Section title="Accessibility & Options">
            <div className="space-y-3">
              {[
                { key: 'is_wheelchair_accessible', label: 'Wheelchair accessible' },
                { key: 'is_ada_compliant',         label: 'ADA compliant' },
                { key: 'has_telehealth',            label: 'Telehealth available' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form[key]} onChange={set(key)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </Section>

          {/* Availability */}
          <Section title="Availability">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className={`relative w-10 h-6 rounded-full transition-colors ${form.is_accepting_referrals ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_accepting_referrals ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <input type="checkbox" checked={form.is_accepting_referrals} onChange={set('is_accepting_referrals')} className="sr-only" />
              <span className="text-sm font-medium text-gray-700">
                {form.is_accepting_referrals ? (
                  <span className="text-green-700">Currently accepting referrals</span>
                ) : (
                  <span className="text-gray-500">Not currently accepting referrals</span>
                )}
              </span>
            </label>

            {/* Self-reported bed availability — only for inpatient facilities */}
            {facility?.has_inpatient && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700 mb-3">Current bed availability <span className="text-gray-400 font-normal">(self-reported)</span></p>
                <p className="text-xs text-gray-400 mb-3">Leave blank for bed types that don't apply to your facility.</p>
                <div className="space-y-2">
                  {BED_TYPES.filter((b) => isLicensedBedType(facility, b.key)).map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-600 flex-1">{label}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>Licensed: {facility[key] || 0}</span>
                        <span>·</span>
                        <span>Available:</span>
                        <input
                          type="number"
                          min={0}
                          max={facility[key] || 99}
                          value={bedAvailability[key] ?? ''}
                          onChange={setBed(key)}
                          className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          placeholder="—"
                        />
                      </div>
                    </div>
                  ))}
                  {BED_TYPES.filter((b) => !isLicensedBedType(facility, b.key)).length > 0 && (
                    <p className="text-xs text-gray-400 pt-1">
                      Only bed types present in your Greenbook record are shown above.
                    </p>
                  )}
                </div>
              </div>
            )}
          </Section>

          <ApiAccessSection facilityId={isAdmin && adminSelected ? adminSelected.id : undefined} />

          {/* Save */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${
              saved
                ? 'bg-green-600 text-white'
                : saving
                  ? 'bg-blue-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </main>
    </div>
  );
}
